import React, { useEffect, useState } from "react";
import {
  Document,
  Image,
  Page,
  PDFViewer,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { Colors } from "../../../assets/Colors";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchGet } from "../../../fetchers";
import LOGO_RKS from "../../../assets/logo/LOGO_RKS.png";
import { useUserBisnisUnit } from "../../../hooks/useUserBisnisUnit";
import angkaTerbilang from "@develoka/angka-terbilang-js";

const SO_UNIT = "U0002";

// Styles yang diperbaiki untuk tabel lebih rapi
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  header: {
    marginBottom: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  logo: {
    width: 120,
    height: 50,
    objectFit: "contain",
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 20,
    textTransform: "uppercase",
    letterSpacing: 1,
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingBottom: 8,
  },
  // Container untuk informasi
  infoContainer: {
    flexDirection: "row",
    marginBottom: 20,
    justifyContent: "space-between",
  },
  leftColumn: {
    width: "45%",
  },
  rightColumn: {
    width: "45%",
    marginLeft: "auto",
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 5,
    alignItems: "flex-start",
  },
  infoLabel: {
    width: 90,
    fontSize: 9,
    fontWeight: "medium",
  },
  infoSeparator: {
    width: 8,
    fontSize: 9,
    textAlign: "center",
  },
  infoValue: {
    flex: 1,
    fontSize: 9,
    fontWeight: "normal",
  },
  
  // Style untuk tabel
  table: {
    marginTop: 20,
    borderWidth: 1,
    borderColor: "#000",
    width: "100%",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    minHeight: 24,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    minHeight: 22,
  },
  // Definisi kolom dengan proporsi yang tepat
  colNo: {
    width: "6%",
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 4,
    fontSize: 8,
    textAlign: "center",
    justifyContent: "center",
  },
  colAccountName: {
    width: "34%",
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 4,
    fontSize: 8,
    textAlign: "left",
    justifyContent: "center",
  },
  colDK: {
    width: "6%",
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 4,
    fontSize: 8,
    textAlign: "center",
    justifyContent: "center",
  },
  colAccountNo: {
    width: "14%",
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 4,
    fontSize: 8,
    textAlign: "center",
    justifyContent: "center",
  },
  colAmountUSD: {
    width: "20%",
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 4,
    fontSize: 8,
    textAlign: "right",
    justifyContent: "center",
  },
  colAmountIDR: {
    width: "20%",
    padding: 4,
    fontSize: 8,
    textAlign: "right",
    justifyContent: "center",
  },
  // Total row
  totalRow: {
    flexDirection: "row",
    backgroundColor: "#f9f9f9",
    minHeight: 24,
    borderTopWidth: 2,
    borderTopColor: "#000",
  },
  totalLabel: {
    width: "60%",
    padding: 4,
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "right",
    borderRightWidth: 1,
    borderRightColor: "#000",
    justifyContent: "center",
  },
  totalAmount: {
    width: "20%",
    padding: 4,
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "right",
    borderRightWidth: 1,
    borderRightColor: "#000",
    justifyContent: "center",
  },
  totalAmountLast: {
    width: "20%",
    padding: 4,
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "right",
    justifyContent: "center",
  },
  
  // Terbilang
  terbilang: {
    marginTop: 15,
    fontSize: 9,
    fontStyle: "italic",
    borderTopWidth: 0.5,
    borderTopColor: "#999",
    paddingTop: 8,
  },
  
  // Signature
  signatureContainer: {
    flexDirection: "row",
    marginTop: 50,
    justifyContent: "space-around",
  },
  signatureBox: {
    width: 120,
    alignItems: "center",
  },
  signatureLabel: {
    fontSize: 8,
    marginBottom: 5,
    fontWeight: "bold",
  },
  signatureLine: {
    width: "100%",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    marginTop: 30,
  },
  signatureName: {
    fontSize: 8,
    marginTop: 5,
  },
  signatureTitle: {
    fontSize: 7,
    color: "#666",
  },
});

// Komponen PDF yang diperbaiki
const BlankoPDF = ({ data }) => {
  if (!data || !data[0]) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text>Data tidak ditemukan</Text>
        </Page>
      </Document>
    );
  }

  const item = data[0];
  
  // Format tanggal
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // Format currency dengan alignment yang rapi
  const formatCurrency = (value) => {
    if (!value || value === "0" || value === "0.00") return "0";
    const num = parseFloat(value);
    return num.toLocaleString("id-ID", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).replace(/,/g, ".");
  };

  // Hitung total
  let total = 0;
  if (item.Data && item.Data.length > 0) {
    item.Data.forEach((d) => {
      total += parseFloat(d.Nominal || 0);
    });
  }
  
  if (total === 0 && item.Nominal) {
    total = parseFloat(item.Nominal);
  }

  const isPayment = item.JenisTransaksi === "Pengeluaran" || item.JenisTransaksi === "Downpayment PO";
  const isBank = (item.JenisCashJournal || "").toLowerCase().includes("bank");
  const title = isPayment
    ? isBank ? "BUKTI BANK KELUAR" : "BUKTI KAS KELUAR"
    : isBank ? "BUKTI BANK MASUK" : "BUKTI KAS MASUK";
  const toFrom = isPayment ? "Dibayarkan Kepada" : "Diterima Dari";

  // Hitung nomor urut
  const getRowNumber = (index) => {
    if (isPayment) {
      let offset = 1;
      if (item.PurchaseOrderID) offset = 2;
      return index + offset;
    } else {
      return index + 2; // baris bank/kas ada di posisi 1
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header dengan Logo */}
        <View style={styles.header}>
          <Image style={styles.logo} src={LOGO_RKS} />
          <Text style={{ fontSize: 10, color: "#666" }}>
            No. Dok: F-KEU-001
          </Text>
        </View>
        
        {/* Title */}
        <Text style={styles.title}>{title}</Text>
        
        {/* Informasi dalam 2 kolom */}
        <View style={styles.infoContainer}>
          {/* Kolom Kiri */}
          <View style={styles.leftColumn}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>{toFrom}</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>{item.DariKepada || "-"}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Alamat</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>-</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Keterangan</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>{item.Keterangan || "-"}</Text>
            </View>
          </View>
          
          {/* Kolom Kanan */}
          <View style={styles.rightColumn}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>No. Bukti</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>{item.CashJournalID || "-"}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Tanggal</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>{formatDate(item.Tanggal)}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Bank/Kas</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>{item.DetailAccount || "-"}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>No. Cek/Giro</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>{item.NoCek || "-"}</Text>
            </View>
          </View>
        </View>
        
        {/* Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={styles.colNo}>No</Text>
            <Text style={styles.colAccountName}>Nama Perkiraan</Text>
            <Text style={styles.colDK}>D/K</Text>
            <Text style={styles.colAccountNo}>No Perkiraan</Text>
            <Text style={styles.colAmountUSD}>Jumlah (USD)</Text>
            <Text style={styles.colAmountIDR}>Jumlah (Rp)</Text>
          </View>
          
          {/* Table Body - Baris PO jika ada (hanya untuk Pengeluaran) */}
          {isPayment && item.PurchaseOrderID && (
            <View style={styles.tableRow}>
              <Text style={styles.colNo}>1</Text>
              <Text style={styles.colAccountName}>Purchase Order: {item.PurchaseOrderID}</Text>
              <Text style={styles.colDK}>D</Text>
              <Text style={styles.colAccountNo}>{item.SumberData || "-"}</Text>
              <Text style={styles.colAmountUSD}>0</Text>
              <Text style={styles.colAmountIDR}>{formatCurrency(item.NominalPO)}</Text>
            </View>
          )}

          {/* Table Body - Baris Bank/Kas untuk Penerimaan (posisi 1, D) */}
          {!isPayment && (
            <View style={styles.tableRow}>
              <Text style={styles.colNo}>1</Text>
              <Text style={styles.colAccountName}>{item.DetailAccount || "-"}</Text>
              <Text style={styles.colDK}>D</Text>
              <Text style={styles.colAccountNo}>{item.DetailCashJournal || "-"}</Text>
              <Text style={styles.colAmountUSD}>0</Text>
              <Text style={styles.colAmountIDR}>{formatCurrency(total)}</Text>
            </View>
          )}

          {/* Table Body - Data Items */}
          {item.Data && item.Data.map((row, idx) => (
            <View style={styles.tableRow} key={idx}>
              <Text style={styles.colNo}>{getRowNumber(idx)}</Text>
              <Text style={styles.colAccountName}>{row.AccountName || "-"}</Text>
              <Text style={styles.colDK}>{isPayment ? "D" : "K"}</Text>
              <Text style={styles.colAccountNo}>{row.Account || "-"}</Text>
              <Text style={styles.colAmountUSD}>0</Text>
              <Text style={styles.colAmountIDR}>{formatCurrency(row.Nominal)}</Text>
            </View>
          ))}

          {/* Table Body - Baris Bank/Kas untuk Pengeluaran (posisi terakhir, K) */}
          {isPayment && (
            <View style={styles.tableRow}>
              <Text style={styles.colNo}>
                {item.Data ? (item.Data.length + (item.PurchaseOrderID ? 1 : 0) + 1) : "2"}
              </Text>
              <Text style={styles.colAccountName}>{item.DetailAccount || "-"}</Text>
              <Text style={styles.colDK}>K</Text>
              <Text style={styles.colAccountNo}>{item.DetailCashJournal || "-"}</Text>
              <Text style={styles.colAmountUSD}>0</Text>
              <Text style={styles.colAmountIDR}>{formatCurrency(total)}</Text>
            </View>
          )}
          
          {/* Total Row */}
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>TOTAL</Text>
            <Text style={styles.totalAmount}>0</Text>
            <Text style={styles.totalAmountLast}>{formatCurrency(total)}</Text>
          </View>
        </View>
        
        {/* Terbilang */}
        <Text style={styles.terbilang}>
          Terbilang : {angkaTerbilang(total)} rupiah
        </Text>
        
        {/* Signature */}
        <View style={styles.signatureContainer}>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>
              {isPayment ? "Penerima," : "Pemberi,"}
            </Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>{item.DariKepada || "-"}</Text>
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Dibuat oleh,</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>{item.CreateBy || "-"}</Text>
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Diperiksa oleh,</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>_________________</Text>
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Disetujui oleh,</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>_________________</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

// Komponen utama (tetap sama seperti sebelumnya)
const BlankoKasBank = () => {
  const { id } = useParams();
  const [formattedId, setFormattedId] = useState("");
  // User SO hanya boleh mencetak blanko dokumen unitnya sendiri (halaman ini bisa
  // dicapai lewat URL langsung). Sejalan dgn halaman detail & report Non PO.
  const { bisnisUnitID: userBisnisUnit, resolved: buResolved } =
    useUserBisnisUnit();

  useEffect(() => {
    if (id) {
      setFormattedId(id.replace(/-/g, "/"));
    }
  }, [id]);

  const { 
    data: blankoData, 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ["blanko", formattedId],
    queryFn: () => fetchGet(`Accounting/CashJournal/${formattedId}`),
    enabled: !!formattedId,
  });

  if (isLoading) {
    return (
      <div className="md:ml-64">
        <div style={{ background: Colors.primary }} className="pt-14 pb-16 px-3 md:px-8 h-auto" />
        <div className="px-3 md:px-8 h-screen -mt-32 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
            <p className="text-gray-600">Memuat data blanko...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="md:ml-64">
        <div style={{ background: Colors.primary }} className="pt-14 pb-16 px-3 md:px-8 h-auto" />
        <div className="px-3 md:px-8 h-screen -mt-32 flex items-center justify-center">
          <div className="text-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p>Error: {error.message}</p>
            </div>
            <button 
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Kembali
            </button>
          </div>
        </div>
      </div>
    );
  }

  const rowBisnisUnit = blankoData?.data?.[0]?.BisnisUnitID || "";
  const isForeignUnit = rowBisnisUnit !== "" && rowBisnisUnit !== SO_UNIT;
  const accessDenied = userBisnisUnit === SO_UNIT && isForeignUnit;
  // Sesi lama: BU user baru diketahui setelah fetch — blanko unit lain ditahan
  // dulu, bukan ditolak (user HO tetap boleh mencetaknya).
  if (accessDenied || (!buResolved && isForeignUnit)) {
    return (
      <div className="md:ml-64">
        <div style={{ background: Colors.primary }} className="pt-14 pb-16 px-3 md:px-8 h-auto" />
        <div className="px-3 md:px-8 h-screen -mt-32 flex items-center justify-center">
          <div className="max-w-md text-center">
            {accessDenied ? (
              <>
                <p className="text-lg font-bold text-gray-800 mb-2">Akses Ditolak</p>
                <p className="text-sm text-gray-600 mb-4">
                  Blanko {formattedId || "ini"} milik Bisnis Unit lain. Akun Anda
                  hanya dapat membuka dokumen SO.
                </p>
                <button
                  onClick={() => window.history.back()}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Kembali
                </button>
              </>
            ) : (
              <p className="text-gray-600">Memeriksa hak akses...</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const hasData = blankoData?.data && blankoData.data.length > 0;

  return (
    <div className="md:ml-64">
      <div style={{ background: Colors.primary }} className="pt-14 pb-16 px-3 md:px-8 h-auto" />
      <div className="px-3 md:px-8" style={{ height: "calc(100vh - 150px)", marginTop: "-100px" }}>
        {hasData ? (
          <PDFViewer 
            style={{ 
              width: "100%", 
              height: "100%", 
              border: "1px solid #ccc",
              borderRadius: "4px"
            }} 
            showToolbar={true}
          >
            <BlankoPDF data={blankoData.data} />
          </PDFViewer>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-gray-500 mb-4">Data tidak tersedia</p>
              <button 
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Kembali
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlankoKasBank;